export type OverlapChecker = (
  from: string,
  to: string | null | undefined,
  excludeId?: number
) => boolean;

/**
 * Core overlap check.
 * Both conditions use non-strict comparison so shared boundaries ARE overlap.
 *
 * Example: [Mar30, Mar31] vs [Mar24, Mar30]
 *   cond1: Mar31 >= Mar24 → true
 *   cond2: Mar30 <= Mar30 → true → overlap ✓
 *
 * Example: [Jan1, Dec31] vs [Dec31, Jun30] (adjacent, new starts when existing ends)
 *   cond1: Dec31 >= Dec31 → true
 *   cond2: Jan1 <= Jun30 → true → overlap ✓
 */
export function hasPeriodOverlap(
  existingFrom: string,
  existingTo: string | null | undefined,
  newFrom: string,
  newTo: string | null | undefined
): boolean {
  const cond1 = !existingTo || existingTo >= newFrom;
  const cond2 = !newTo || existingFrom <= newTo;
  return cond1 && cond2;
}

/**
 * Check if a new period overlaps with any existing clinic_information records.
 */
export function checkOverlapWithExistingInfos(
  existingInfos: Array<{ id: number; available_from: string | null; available_to: string | null }>,
  newFrom: string,
  newTo: string | null | undefined,
  excludeId?: number
): boolean {
  return existingInfos.some(r => {
    if (excludeId !== undefined && r.id === excludeId) return false;
    return hasPeriodOverlap(r.available_from ?? '', r.available_to, newFrom, newTo);
  });
}

import type { ConfigType, Dayjs } from '@shared/lib/dayjs';

/**
 * Convert a raw form value (Dayjs | string | number | Date | null | undefined) to a YYYY-MM-DD string, or null.
 */
export function toDateStr(val: ConfigType): string | null {
  if (!val) return null;
  if (typeof val === 'object' && 'format' in val) {
    return (val as Dayjs).format('YYYY-MM-DD');
  }
  if (val instanceof Date) {
    return val.toISOString().split('T')[0];
  }
  if (typeof val === 'string' && val.trim()) return val.trim();
  return null;
}
